import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo, } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Grid, Typography, Modal, alpha, } from '@mui/material';
import { useStore } from '@mcn-platform/models';
import { WysiwygViewer } from '../../publisher/wysiwyg/WysiwygEditor';
import AssetVideoPlayer from '../common/asset/AssetVideoPlayer';
import Notes from '../class/notes/Notes';
import KickerWidget from '../kicker/KickerWidget';
import formatSeconds from '../common/formatSeconds';
import { configMeetingPage } from '../common/portalConfig';
import AppBar from '../nav/AppBar';
import MeetingSummary from './MeetingSummary';
import { defaultPostMeetingMessage } from './constants';
import TabsComponent from './TabsComponent';
import MeetingBar from './MeetingBar';
const PostMeeting = ({ meeting, registration, settings, isLoading, }) => {
    const store = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const rootPath = '.';
    const recordingUnavailable = !meeting.recordingMediaPipelineID;
    const [error, setError] = useState(null);
    const [hasRecording, setHasRecording] = useState(!!meeting.recording);
    const isMountedRef = useRef(true);
    const meetingKeyRef = useRef(meeting.key);
    const [currentPosition, setCurrentPosition] = useState(0);
    const [seekPosition, setSeekPosition] = useState(undefined);
    const [seekTrigger, setSeekTrigger] = useState(undefined);
    useEffect(() => {
        const currentRef = meetingKeyRef.current;
        isMountedRef.current = true;
        if (!hasRecording && !recordingUnavailable) {
            store.startPollingModel('meeting', currentRef, 10000, (updatedMeeting) => {
                if (isMountedRef.current && updatedMeeting.recording) {
                    setHasRecording(true);
                    store.stopPollingModel('meeting', currentRef);
                }
            });
        }
        return () => {
            isMountedRef.current = false;
            store.stopPollingModel('meeting', currentRef);
        };
    }, [hasRecording, store]);
    const { buttonLabel, action, instructorAction } = useMemo(() => configMeetingPage(store, meeting, registration, navigate, location, rootPath), [meeting, registration]);
    const handleAction = action ? async () => {
        try {
            await action();
            setError(null);
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred during the action.';
            setError(errorMessage);
        }
    } : undefined;
    const makeReference = () => ([
        `${meeting?.id}/video/${currentPosition}`,
        `${meeting?.name}/Video/${formatSeconds(currentPosition)}`,
    ]);
    const handleViewNote = (note) => {
        const { reference } = note;
        const referenceDetails = reference?.split('/') || [];
        const meetingID = referenceDetails[0];
        const timestamp = parseFloat(referenceDetails[2]);
        if (meetingID === meeting.id) {
            setSeekPosition(timestamp);
            setSeekTrigger(Date.now());
        }
    };
    const getSetting = (name) => {
        if (settings && settings[name] !== undefined) {
            return settings[name];
        }
        return PostMeeting.defaultProps.settings[name];
    };
    const showNotes = getSetting('showNotes');
    const showAgenda = getSetting('showAgenda');
    const showKicker = getSetting('showKicker');
    if (isLoading) {
        return (_jsx("div", { style: { textAlign: 'center', marginTop: '2rem' }, children: _jsx(CircularProgress, {}) }));
    }
    if (!registration) {
        return (_jsxs("div", { children: [error && (_jsx(Typography, { variant: "body1", color: "error", align: "center", children: error })), _jsx(MeetingSummary, { meeting: meeting, buttonLabel: buttonLabel, action: handleAction, instructorAction: instructorAction, settings: settings })] }));
    }
    if (!hasRecording && !recordingUnavailable) {
        const postMeetingMessage = meeting?.postMeetingMessage || defaultPostMeetingMessage;
        return (_jsx("div", { style: { textAlign: 'center', marginTop: '2rem' }, children: postMeetingMessage && (_jsx(Box, { sx: { mt: 4, mb: 4 }, children: _jsx(WysiwygViewer, { id: `meeting-content-${meeting.key}`, value: postMeetingMessage.slate }) })) }));
    }
    return (_jsx(Modal, { open: true, children: _jsx(Box, { sx: {
                position: 'absolute',
                overflowY: 'scroll',
                maxHeight: '100%',
                minHeight: '100%',
                width: '100%',
                backgroundColor: (t) => (alpha(t?.palette?.background?.default || '#ffffff', 1)),
            }, children: _jsxs(Grid, { container: true, children: [_jsxs(Grid, { item: true, xs: 12, children: [_jsx(AppBar, { limitToPageWidth: true }), _jsx(MeetingBar, { name: meeting.name })] }), _jsxs(Grid, { item: true, xs: 12, md: 9, sx: { p: 2 }, children: [recordingUnavailable ? (_jsx(Typography, { variant: "h6", align: "center", color: "textPrimary", children: "No recording is available for this meeting." })) : (_jsx(AssetVideoPlayer, { asset: meeting.recording, assetPoster: meeting.image, showControls: true, aspectRatio: "16:9", playerStyle: { objectFit: 'contain' }, seekPosition: seekPosition, seekTrigger: seekTrigger, onProgress: setCurrentPosition, onEnd: () => { } })), showAgenda && (_jsx(Box, { sx: { mt: 4 }, children: _jsx(WysiwygViewer, { id: `meeting-content-${meeting.key}`, value: meeting.content?.slate }) })), _jsx(Box, { sx: { mt: 4 }, children: _jsx(TabsComponent, { meeting: meeting, settings: settings }) })] }), _jsxs(Grid, { item: true, xs: 12, md: 3, sx: { p: 2 }, children: [showKicker && (_jsx(Box, { sx: { mt: 2 }, children: _jsx(KickerWidget, { kickerID: meeting.kicker?.id, limitToPageWidth: true }) })), showNotes && (_jsx(Notes, { isCourseItemSelected: true, progress: registration, modelName: "meetingRegistration", handleViewNote: handleViewNote, makeReference: makeReference }))] })] }) }) }));
};
export default observer(PostMeeting);
PostMeeting.defaultProps = {
    settings: {
        showNotes: true,
        showTranscript: true,
        showAgenda: true,
        showKicker: true,
    },
    registration: undefined,
    isLoading: false,
};
//# sourceMappingURL=PostMeeting.js.map